import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, Share2, Users, Trash2 } from "lucide-react";
import { API } from "../constants";

export const SharePage = () => {
  const [shares, setShares] = useState({ my_shares: [], shared_with_me: [] });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchShares();
  }, []);

  const fetchShares = async () => {
    try {
      const res = await axios.get(`${API}/shares`);
      setShares(res.data);
    } catch (err) {
      toast.error("Error al cargar compartidos");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 3) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/users/search?q=${searchQuery}`);
      setSearchResults(res.data);
    } catch (err) {
      toast.error("Error en búsqueda");
    } finally {
      setSearching(false);
    }
  };

  const handleShare = async (email) => {
    try {
      await axios.post(`${API}/shares`, { shared_with_email: email });
      toast.success("Lista compartida exitosamente");
      setSearchQuery("");
      setSearchResults([]);
      fetchShares();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al compartir");
    }
  };

  const handleRemoveShare = async (shareId) => {
    try {
      await axios.delete(`${API}/shares/${shareId}`);
      toast.success("Compartido eliminado");
      fetchShares();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="share-page">
      <header className="sticky top-0 z-40 glass-nav p-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate(-1)} data-testid="back-btn">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-xl font-semibold">Compartir Mi Lista</h1>
        </div>
      </header>

      <main className="p-4 max-w-2xl mx-auto">
        {/* Search Users */}
        <section className="glass rounded-2xl p-4 mb-6">
          <h2 className="text-lg font-semibold mb-4">Compartir con Usuario</h2>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre o email..."
              className="form-input flex-1"
              data-testid="share-search-input"
            />
            <button
              onClick={handleSearch}
              disabled={searching || searchQuery.length < 3}
              className="px-4 py-2 bg-amber-500 text-black font-medium rounded-xl disabled:opacity-50"
              data-testid="search-users-btn"
            >
              Buscar
            </button>
          </div>
          
          {searchResults.length > 0 && (
            <div className="space-y-2">
              {searchResults.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-zinc-900 rounded-xl">
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-sm text-zinc-400">{user.email}</p>
                  </div>
                  <button
                    onClick={() => handleShare(user.email)}
                    className="px-3 py-1 bg-emerald-600 text-white text-sm rounded-lg"
                    data-testid={`share-with-${user.id}`}
                  >
                    Compartir
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* My Shares */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Share2 size={20} className="text-amber-500" /> Compartido con
          </h2>
          {shares.my_shares.length === 0 ? (
            <p className="text-zinc-400 text-center py-8">No has compartido tu lista con nadie</p>
          ) : (
            <div className="space-y-3">
              {shares.my_shares.map((share) => (
                <div key={share.id} className="glass rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{share.shared_with_name}</p>
                    <p className="text-sm text-zinc-400">{share.shared_with_email}</p>
                  </div>
                  <button
                    onClick={() => handleRemoveShare(share.id)}
                    className="text-zinc-500 hover:text-red-400"
                    data-testid={`remove-share-${share.id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Shared With Me */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Users size={20} className="text-emerald-500" /> Compartido Conmigo
          </h2>
          {shares.shared_with_me.length === 0 ? (
            <p className="text-zinc-400 text-center py-8">Nadie ha compartido su lista contigo</p>
          ) : (
            <div className="space-y-3">
              {shares.shared_with_me.map((share) => (
                <div key={share.id} className="glass rounded-xl p-4">
                  <p className="font-medium">{share.owner_name}</p>
                  <p className="text-sm text-zinc-400">{share.shared_with_email}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default SharePage;
